import {
  EVENT_POSITION,
  EVENT_COMPLETE,
  EVENT_DURATION_CHANGE,
  EVENT_LOADED_METADATA,
  EVENT_MUTE,
  EVENT_PAUSE,
  EVENT_STOP,
  EVENT_PLAYING,
  EVENT_PLAYLIST_ITEM_CHANGE,
  EVENT_RESUME,
  EVENT_SEEK,
  EVENT_UNMUTE,
  EVENT_VOLUME_CHANGE,
  EVENT_AD_SKIP,
  EVENT_AD_END,
  EVENT_DOWNLOAD
} from '../constants';
import Events from '../../utils/events';
import logger from '../../utils/logger';
import { Tracker } from '../tracking';
import nextTick from '../../utils/nextTick';

let lastTrackerId = 0;

/**
 * Provides methods for tracking events triggered on a player
 */
export default class TrackingController {
  constructor (player, options = {}) {
    this.player = player;

    this.settings = Object.assign({}, options);

    // holds the id of the active tracker
    this.tracker = null;

    // holds the id of the active content tracker
    this.contentTracker = null;

    // holds a mapping of tracker ids to tracker objects
    this.trackers = {};

    this.logger = options.logger || logger;

    // Ensure the handleEvent method is always bound to this
    this.handleEvent = this.handleEvent.bind(this);

    // Bind event listeners to the player
    this.bindEvents();
  }

  getTrackingContext () {
    const context = Object.create(null);

    // playback context
    context.position = this.player.getPosition();
    context.duration = this.player.getDuration();
    context.muted = this.player.getMute();
    context.volume = this.player.getVolume();
    context.playbackRate = this.player.getPlaybackRate();

    // source context
    const source = this.player.getSource();
    context.quality = source.quality;
    context.format = source.format;
    context.bitrate = source.bitrate;
    context.source = source.url;

    // metadata context
    const playlistItem = this.player.playlist.getPlaylistItem();
    const metadata = (playlistItem && playlistItem.metadata) || {};

    context.contentType = metadata.contentType;
    context.uid = metadata.uid || source.url;
    context.creator = metadata.creator;
    context.grouping = metadata.grouping;
    context.publishDate = metadata.publishDate;
    context.title = metadata.title;
    context.tags = (playlistItem && playlistItem.tags) || [];

    return context;
  }

  /**
   * Retrieve a tracker by its tracker id
   *
   * @param  {String} trackerId A tracker id
   * @return {Tracker|null}
   */
  getTracker (trackerId) {
    if (typeof trackerId === 'undefined') {
      return this.getTracker(this.tracker);
    }
    if (this.trackers[trackerId]) {
      return this.trackers[trackerId];
    }
    return null;
  }

  /**
   * Retrieve a trackers id
   *
   * @param  {Tracker} tracker A tracker object
   * @return {String}
   */
  getTrackerId (tracker) {
    return tracker.id;
  }

  /**
   * Adds a tracker to the list of trackers
   *
   * @param {Tracker} tracker The tracker to be added
   */
  addTracker (tracker) {
    tracker.id = ++lastTrackerId;
    this.trackers[tracker.id] = tracker;
  }

  /**
   * Sets the active tracker
   *
   * @param {Tracker} tracker The tracker to be activated
   */
  setTracker (tracker) {
    if (tracker === null) {
      this.tracker = null;
      return;
    }

    const trackerId = this.getTrackerId(tracker);
    // add this tracker to the trackers list if it isn't present yet
    if (!this.trackers[trackerId]) {
      this.addTracker(tracker);
    }
    this.tracker = tracker.id;
  }

  /**
   * Create a new tracker instance
   *
   * @param  {Object} playlistItem The playlist item (or ad) to build a tracker for
   *
   * @return {Tracker}
   */
  createTracker (playlistItem) {
    const tracker = new Tracker(this.player, playlistItem);
    return tracker;
  }

  /**
   * Sets the active content tracker
   *
   * @param {Tracker} tracker The conetnt tracker to be activated
   */
  setContentTracker (tracker) {
    const trackerId = this.getTrackerId(tracker);
    // add this tracker to the trackers list if it isn't present yet
    if (!this.trackers[trackerId]) {
      this.addTracker(tracker);
    }
    this.contentTracker = tracker.id;
  }

  /**
   * Restores the content tracker after a static ad
   */
  restoreContentTracker () {
    if (this.tracker !== this.contentTracker) {
      nextTick(_ => { this.setTracker(this.getTracker(this.contentTracker)); });
    }
  }

  /**
   * Bind event handlers to player events
   */
  bindEvents () {
    Events.on(this.player, [
      EVENT_RESUME,
      EVENT_PAUSE,
      EVENT_STOP,
      EVENT_MUTE,
      EVENT_UNMUTE,
      EVENT_POSITION,
      EVENT_SEEK,
      EVENT_LOADED_METADATA,
      EVENT_PLAYLIST_ITEM_CHANGE,
      EVENT_DURATION_CHANGE,
      EVENT_PLAYING,
      EVENT_COMPLETE,
      EVENT_VOLUME_CHANGE,
      EVENT_AD_SKIP,
      EVENT_AD_END,
      EVENT_DOWNLOAD
    ], this.handleEvent);
  }

  /**
   * Unbind event handlers from player events
   */
  unbindEvents () {
    Events.off(this.player, [
      EVENT_RESUME,
      EVENT_PAUSE,
      EVENT_STOP,
      EVENT_MUTE,
      EVENT_UNMUTE,
      EVENT_POSITION,
      EVENT_SEEK,
      EVENT_LOADED_METADATA,
      EVENT_PLAYLIST_ITEM_CHANGE,
      EVENT_DURATION_CHANGE,
      EVENT_PLAYING,
      EVENT_COMPLETE,
      EVENT_VOLUME_CHANGE,
      EVENT_AD_SKIP,
      EVENT_AD_END,
      EVENT_DOWNLOAD
    ], this.handleEvent);
  }

  /**
   * Handles events triggered by the player
   *
   * @param {object} event An event triggered by the player
   */
  handleEvent (event) {
    // create a new tracker for the new playlist item
    // @todo only create a new tracker if there isn't one already for the playlist item
    if (event.type === EVENT_PLAYLIST_ITEM_CHANGE) {
      return null;
    }
    // update the static ad markers when the content duration changes
    if (event.type === EVENT_DURATION_CHANGE) {
      return null;
    }
    // restore the content tracker when the user skips an ad
    if (event.type === EVENT_AD_SKIP) {
      this.track(event);
      return null;
    }
    if (event.type === EVENT_AD_END) {
      return null;
    }
    // ignore seek events on live streams
    if (event.type === EVENT_SEEK && this.player.isLive()) {
      return null;
    }

    this.track(event);

    // clear the tracker of all entries except the content tracker when stopping live sources
    if (event.type === EVENT_STOP && this.player.isLive()) {
      this.reset();
    }
  }

  /**
   * Track a new event on the current tracker
   * @param  {Object} event An event to be tracked
   */
  track (event) {
    // only handle tracking while enabled
    const tracker = this.getTracker();
    if (!tracker) return;
    const context = this.getTrackingContext();
    tracker.track(event, context);
  }

  /**
   * Reset tracking to the point before playback starts - useful for resetting the tracker after a stream is paused
   */
  reset () {
    this.clear();

    const playlistItem = this.player.playlist.getPlaylistItem();

    if (playlistItem) {
      const tracker = new Tracker(this.player, playlistItem);
      this.setContentTracker(tracker);
      this.setTracker(tracker);
    }
  }

  /**
   * Clear the text track used to schedule percentile tracking events
   */
  clear () {
    this.trackers = {};
    this.tracker = null;
    this.contentTracker = null;
  }

  /**
   * Destroys the controller instance
   */
  destroy () {
    this.unbindEvents();
    this.tracker = null;
    this.contentTracker = null;
  }
}
